/**
 * React Query hooks for fetching cooling system configurations
 * @see GitLab #996 — Repeating Sections (Phase 2: Cooling)
 */

import { useQuery } from '@tanstack/react-query';

import { isRepeatingCollectionRouteNotFound } from './repeatingCollectionRouteNotFound';
import { useNetworkStatus } from './useOfflineRequestsQuery';
import { groupCoolingSystemsByStatus } from '../../components/coolingsystems/coolingSystemListUtils';
import { api } from '../../services/api';
import {
  applyPendingCoolingSystemOperations,
  getCachedCoolingSystemList,
  getOfflineCoolingSystemById,
  getOfflineCoolingSystemGrouped,
  getOfflineCoolingSystemList,
  getInFlightCoolingSystemOpsForRequest,
  mirrorCoolingSystemToOfflineRequestCache,
} from '../../services/coolingSystemOfflineSync';

import type {
  CoolingSystemConfiguration,
  CoolingSystemConfigurationsResponse,
  GroupedCoolingSystemConfigurations,
} from '../../types/coolingSystem';

/**
 * Query keys for consistent cache management
 */
export const coolingSystemConfigurationsKeys = {
  all: ['coolingSystemConfigurations'] as const,
  lists: () => [...coolingSystemConfigurationsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...coolingSystemConfigurationsKeys.lists(), requestId] as const,
  grouped: (requestId: string) =>
    [...coolingSystemConfigurationsKeys.all, 'grouped', requestId] as const,
  details: () => [...coolingSystemConfigurationsKeys.all, 'detail'] as const,
  detail: (requestId: string, coolingSystemId: number) =>
    [
      ...coolingSystemConfigurationsKeys.details(),
      requestId,
      coolingSystemId,
    ] as const,
};

/**
 * Hook for fetching all cooling system configurations for a request
 * Includes offline fallback to return cached configurations
 */
export const useCoolingSystemConfigurations = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: coolingSystemConfigurationsKeys.list(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineCoolingSystemList(requestId);
      }

      try {
        const response = await api.get<CoolingSystemConfigurationsResponse>(
          `/requests/${requestId}/cooling-systems`,
        );
        await mirrorCoolingSystemToOfflineRequestCache(
          requestId,
          response.coolingSystems,
        );
        const pendingOps = await getInFlightCoolingSystemOpsForRequest(
          requestId,
        );
        return applyPendingCoolingSystemOperations(
          requestId,
          response.coolingSystems,
          pendingOps,
        );
      } catch (error) {
        if (isRepeatingCollectionRouteNotFound(error)) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) =>
      isRepeatingCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch cooling system configurations.',
    },
  });
};

/**
 * Hook for fetching cooling system configurations grouped by status (existing vs new)
 * Includes offline fallback (derived from cached list)
 */
export const useCoolingSystemConfigurationsGrouped = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: coolingSystemConfigurationsKeys.grouped(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineCoolingSystemGrouped(requestId);
      }

      const response = await api.get<GroupedCoolingSystemConfigurations>(
        `/requests/${requestId}/cooling-systems/grouped`,
      );
      const allUnits = [...response.existing, ...response.new];
      await mirrorCoolingSystemToOfflineRequestCache(requestId, allUnits);
      const pendingOps = await getInFlightCoolingSystemOpsForRequest(requestId);
      return groupCoolingSystemsByStatus(
        applyPendingCoolingSystemOperations(requestId, allUnits, pendingOps),
      );
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch grouped cooling system configurations.',
    },
  });
};

/**
 * Hook for fetching a single cooling system configuration
 * Includes offline fallback from cached request units
 */
export const useCoolingSystemConfiguration = (
  requestId: string,
  coolingSystemId: number,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: coolingSystemConfigurationsKeys.detail(
      requestId,
      coolingSystemId,
    ),
    queryFn: async () => {
      if (!isOnline) {
        const cached = await getOfflineCoolingSystemById(
          requestId,
          coolingSystemId,
        );
        if (!cached) {
          throw new Error(
            'Cooling system configuration not found in offline cache',
          );
        }
        return cached;
      }

      const response = await api.get<CoolingSystemConfiguration>(
        `/requests/${requestId}/cooling-systems/${coolingSystemId}`,
      );
      const list = await getCachedCoolingSystemList(requestId);
      const merged = list.some(u => u.id === response.id)
        ? list.map(u => (u.id === response.id ? response : u))
        : [...list, response];
      await mirrorCoolingSystemToOfflineRequestCache(requestId, merged);
      return response;
    },
    enabled: !!requestId && !!coolingSystemId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch cooling system configuration details.',
    },
  });
};
